<template>
  <div class="metadata-display px-2 d-flex flex-grow-1 flex-wrap">
    <div
      v-if="document.metadata"
      class="metadata-summary d-flex my-3 mx-auto flex-column"
    >
      <div class="d-flex align-items-center justify-content-between">
        <h6 class="m-0">Metadata summary</h6>
        <b-button
          class="pr-0"
          variant="link"
          data-testid="metadata-toggle"
          @click="toggleAdvanced"
        >
          <span v-if="!showAdvanced">Show all</span>
          <span v-else>Show less</span>
        </b-button>
      </div>
      <div class="card p-4">
        <div
          v-for="signal in fraudulentMetadata"
          :key="signal.signalName"
          :class="[
            'signal-group d-flex px-2 pt-3 pb-2',
            { active: $route.query.signal === signal.signalName },
          ]"
        >
          <signal-summary
            class="mb-2"
            :detailed="true"
            :metadata="flattenedMetadata"
            :signal="signal"
          />
        </div>
      </div>
      <div v-if="showAdvanced" class="mt-4 d-flex flex-column">
        <h6>All metadata</h6>
        <div>
          <b-table
            :fields="metadataTableKeys"
            :responsive="true"
            :items="sortedMetadata"
            data-testid="metadata-table"
            class="ui-table metadata-table card"
          >
            <template #cell(value)="{ item: { value } }">
              <metadata-value :metadata-items="value" />
            </template>
          </b-table>
        </div>
      </div>
    </div>
    <div v-else class="d-flex w-100 align-items-center justify-content-center">
      <i-spinner></i-spinner>
    </div>
  </div>
</template>

<script>
import { METADATA } from '../../constants/signalTypes';
import { DATE_METADATA, SOFTWARE_BLOCKLIST } from '../../constants/signalNames';
import SignalSummary from './document-view/signals/SignalSummary';
import { flattenMetadata, sortMetadata } from '../../utils/metadata';
import MetadataValue from './MetadataValue';
import { iSpinner } from '@inscribe/signal';

export default {
  components: {
    'metadata-value': MetadataValue,
    'signal-summary': SignalSummary,
    iSpinner,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showAdvanced: false,
      metadataTableKeys: {
        property: { key: 'label', label: 'Property' },
        value: { key: 'value', label: 'Value', tdClass: 'fs-exclude' },
      },
    };
  },
  computed: {
    flattenedMetadata() {
      return flattenMetadata(this.document.metadata);
    },
    sortedMetadata() {
      return sortMetadata(
        Object.entries(this.flattenedMetadata).map(([label, value]) => ({
          label,
          value,
        })),
      );
    },
    fraudulentMetadata() {
      return Object.entries(this.document.fraud_results || []).reduce(
        (signals, [signalName, signal]) => {
          if (
            (signal.failed ||
              signalName === DATE_METADATA ||
              signalName === SOFTWARE_BLOCKLIST) &&
            signal.type === METADATA
          ) {
            return [
              ...signals,
              { signalName, failed: signal.failed, ...signal.details },
            ];
          }

          return signals;
        },
        [],
      );
    },
  },
  methods: {
    toggleAdvanced() {
      this.showAdvanced = !this.showAdvanced;
    },
  },
};
</script>

<style lang="scss">
.metadata-display {
  max-height: 100%;
  overflow: auto;

  .signal-group.active {
    background: $color-dark-blue-10;
    border-radius: $border-radius-default;
  }

  .metadata-summary {
    max-width: 600px;
    width: 100%;

    .card {
      overflow-x: auto;
    }
  }

  .metadata-table {
    font-size: $font-size-secondary;

    .metadata-value {
      max-width: 375px;
    }
  }
}
</style>
